import { ROUTES } from 'constants/routes';

const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_AUTH_SCOPES = 'openid email profile';

const AUTH_LANDING_PATHS = new Set<string>([
  ROUTES.signIn,
  ROUTES.signUp,
  ROUTES.ntuSignIn,
  ROUTES.ntuSignUp,
  ROUTES.welcome,
  ROUTES.googleAuth,
]);

export interface IGoogleOAuthState {
  returnTo?: string;
  from?: {
    pathname: string;
    search?: string;
  };
}

// Must be added to Google Cloud Console → Authorized redirect URIs.
export const getGoogleRedirectUri = () =>
  `${window.location.origin}${ROUTES.googleAuth}`;

export const isSafeInternalPath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//');

const toBase64 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const fromBase64 = (value: string) => {
  const binary = window.atob(value);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder().decode(bytes);
};

export const encodeGoogleOAuthState = (state: IGoogleOAuthState) => {
  const base64 = toBase64(JSON.stringify(state));

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

export const decodeGoogleOAuthState = (
  raw?: string | null
): IGoogleOAuthState => {
  if (!raw) {
    return {};
  }

  try {
    const padded = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (padded.length % 4)) % 4;
    const json = fromBase64(padded + '='.repeat(padLength));
    const parsed = JSON.parse(json) as IGoogleOAuthState;

    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const buildGoogleAuthUrl = (params: {
  clientId: string;
  redirectUri: string;
  state: string;
}) => {
  const url = new URL(GOOGLE_AUTH_ENDPOINT);

  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', GOOGLE_AUTH_SCOPES);
  url.searchParams.set('state', params.state);
  url.searchParams.set('prompt', 'select_account');
  url.searchParams.set('include_granted_scopes', 'true');

  return url.toString();
};

export const resolveGoogleAuthRedirect = (state: IGoogleOAuthState) => {
  const fromPath = state.from?.pathname
    ? `${state.from.pathname}${state.from.search || ''}`
    : '';

  if (fromPath && isSafeInternalPath(fromPath)) {
    return fromPath;
  }

  if (state.returnTo && isSafeInternalPath(state.returnTo)) {
    const pathname = state.returnTo.split('?')[0];

    if (!AUTH_LANDING_PATHS.has(pathname)) {
      return state.returnTo;
    }
  }

  return ROUTES.main;
};

export const resolveGoogleAuthFallback = (state: IGoogleOAuthState) => {
  if (state.returnTo && isSafeInternalPath(state.returnTo)) {
    const pathname = state.returnTo.split('?')[0];

    if (AUTH_LANDING_PATHS.has(pathname) && pathname !== ROUTES.googleAuth) {
      return pathname;
    }
  }

  return ROUTES.signIn;
};
